import type { ContextCheck, ContextHint, ContextHintOption, ContextSourceType } from "./api";

/**
 * Re-evaluates the latest build's "consider" checks against the answers on the
 * screen. A mirror of the server's `evaluateContextChecks`
 * (packages/api/src/ai/visit-context-snapshot.ts) — same rules, so a tag the
 * nurse sees while answering is the tag the ground truth records:
 *
 *   unanswered                         → omission      "Not yet answered — discharge summary notes O2 2 L/min"
 *   answered, meets every expectation  → no tag
 *   answered, breaks one               → contradiction "Differs from CMS-485 plan of care: wheelchair not checked (Sep 1)"
 *                                        (change, when the report is a previous visit's)
 *   answered, nothing to compare       → a model-judged check stays; a mapped one drops
 *
 * An expectation is one answer option the source affirmed (must be selected)
 * or explicitly denied (must not be). Options the source never mentioned are
 * neutral — silence is not a denial.
 *
 * Nothing here blocks and nothing is dismissed: the tag follows the answer.
 */

function answerTokens(raw: string): string[] {
    const norm = (v: unknown) => String(v).trim().toLowerCase();
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.map(norm).filter((t) => t !== "");
        }
        if (parsed && typeof parsed === "object") {
            return Object.values(parsed as Record<string, unknown>).map(norm);
        }
        return [norm(parsed)];
    } catch {
        return [norm(raw)];
    }
}

/** An answer option as the comparison needs it: what is stored (`value`) and what is shown (`label`). */
export interface AgreeOption {
    value: string;
    label: string;
}

/** Per question id, its options — built from the section's `Question[]` (see `optionsLookup`). */
export type QuestionOptionsLookup = {
    get(questionId: number): { options: ReadonlyArray<AgreeOption> } | undefined;
};

/** The lookup `evaluateContextChecks` wants, from the section's questions. */
export function optionsLookup(
    questions: ReadonlyArray<{
        id: string | number;
        answerOptions?: ReadonlyArray<{ value: string; text: string }> | null;
    }>
): QuestionOptionsLookup {
    const map = new Map<number, { options: AgreeOption[] }>();
    for (const q of questions) {
        map.set(Number(q.id), {
            options: (q.answerOptions ?? []).map((o) => ({ value: o.value, label: o.text }))
        });
    }
    return map;
}

function foldOption(s: string): string {
    return s.trim().toLowerCase().replace(/\s+/g, " ").replace(/[.]+$/, "");
}

/**
 * Mirror of the server's `normalizeAgreeValues`: map strings (the model
 * copies option LABELS; the answer is stored as the VALUE) onto the
 * question's option values, dropping what matches neither. A question
 * without options keeps the strings as typed.
 */
export function normalizeAgreeValues(
    question: { options: ReadonlyArray<AgreeOption> } | null | undefined,
    values: ReadonlyArray<string>
): string[] {
    const out: string[] = [];
    const push = (v: string) => {
        if (v !== "" && !out.includes(v)) {
            out.push(v);
        }
    };
    const options = question?.options ?? [];
    if (options.length === 0) {
        for (const v of values) {
            push(String(v).trim());
        }
        return out;
    }
    for (const v of values) {
        const key = foldOption(String(v));
        if (key === "") {
            continue;
        }
        const hit = options.find((o) => foldOption(o.value) === key || foldOption(o.label) === key);
        if (hit) {
            push(hit.value);
        }
    }
    return out;
}

function sameValue(a: string, b: string, freeText = false): boolean {
    const x = a.trim().toLowerCase();
    const y = b.trim().toLowerCase();
    if (x === y) {
        return true;
    }
    const xn = Number(x);
    const yn = Number(y);
    if (x !== "" && y !== "" && !Number.isNaN(xn) && !Number.isNaN(yn) && xn === yn) {
        return true;
    }
    // A free-text answer agrees when either contains the other (server mirror).
    return freeText && x.length >= 3 && y.length >= 3 && (x.includes(y) || y.includes(x));
}

/** The answer's selected values (JSON array → multi-select), plus their option values. */
function selectedValues(
    raw: string,
    question?: { options: ReadonlyArray<AgreeOption> } | null
): { values: string[]; multi: boolean } {
    const tokens = answerTokens(raw);
    const values = [...tokens];
    if ((question?.options.length ?? 0) > 0) {
        for (const v of normalizeAgreeValues(question, tokens)) {
            values.push(v.toLowerCase());
        }
    }
    return { values, multi: raw.trim().startsWith("[") };
}

/**
 * Mirror of the server's `failedExpectations`: multi-select — every affirmed
 * option selected, no denied one; single choice / yes-no / number / text —
 * one of the affirmed values (when any), none of the denied.
 */
export function failedExpectations(
    raw: string,
    expects: ReadonlyArray<ContextHintOption>,
    question?: { options: ReadonlyArray<AgreeOption> } | null
): ContextHintOption[] {
    if (expects.length === 0) {
        return [];
    }
    const { values, multi } = selectedValues(raw, question);
    const freeText = (question?.options.length ?? 0) === 0;
    const has = (v: string) => values.some((sel) => sameValue(sel, v, freeText));
    const affirmed = expects.filter((e) => e.present);
    const denied = expects.filter((e) => !e.present);
    const failed: ContextHintOption[] = [];
    if (multi) {
        failed.push(...affirmed.filter((e) => !has(e.value)));
    } else if (affirmed.length > 0 && !affirmed.some((e) => has(e.value))) {
        failed.push(...affirmed);
    }
    failed.push(...denied.filter((e) => has(e.value)));
    return failed;
}

/**
 * The per-answer messages to show for what the nurse has selected: every
 * option message whose option is in the answer. This is how a DENIED option
 * surfaces — only once she selects it; affirmed ones already show under the
 * question.
 *
 * A denial that CONFLICTS with another source is NOT held back: the hint shows
 * it from the start (`upfrontOptionMessages`), because the disagreement is what
 * the nurse needs, not the denial on its own.
 */
export function selectedOptionMessages(
    hints: ReadonlyArray<ContextHint>,
    answer: string | undefined | null,
    question?: { options: ReadonlyArray<AgreeOption> } | null
): ContextHintOption[] {
    if (!isAnswered(answer)) {
        return [];
    }
    const { values } = selectedValues(String(answer), question);
    const out: ContextHintOption[] = [];
    for (const hint of hints) {
        for (const o of hint.options ?? []) {
            // A conflicting option is already shown under the question.
            if (o.conflict) {
                continue;
            }
            if (
                values.some((sel) => sameValue(sel, o.value)) &&
                !out.some((x) => x.text === o.text)
            ) {
                out.push(o);
            }
        }
    }
    return out;
}

/** What a hint shows from the start: what the source affirms, plus anything in conflict. */
export function upfrontOptionMessages(hint: ContextHint): ContextHintOption[] {
    return (hint.options ?? []).filter((o) => o.present || o.conflict != null);
}

/** Checks stored before `expects` existed: agreeing values read as affirmed options. */
function expectsOf(
    c: ContextCheck,
    question?: { options: ReadonlyArray<AgreeOption> } | null
): ContextHintOption[] {
    if (Array.isArray(c.expects) && c.expects.length > 0) {
        if ((question?.options.length ?? 0) === 0) {
            return c.expects;
        }
        return c.expects.flatMap((e) => {
            const v = normalizeAgreeValues(question, [e.value]);
            return v.length ? [{ ...e, value: v[0] }] : [];
        });
    }
    const legacy = Array.isArray(c.agreeValues) ? c.agreeValues : [];
    const values =
        (question?.options.length ?? 0) > 0 ? normalizeAgreeValues(question, legacy) : legacy;
    return values.map((value) => ({
        value,
        label: question?.options.find((o) => o.value === value)?.label ?? value,
        present: true,
        clause: c.clause,
        text: hintLine(c)
    }));
}

function capitalize(s: string): string {
    return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

function lowerFirst(s: string): string {
    return /^[A-Z][a-z]/.test(s) ? s[0].toLowerCase() + s.slice(1) : s;
}

function attributionPrefix(type: ContextSourceType, label: string): string {
    switch (type) {
        case "questionnaire":
            return `${capitalize(label)} reports`;
        case "document":
            return `${capitalize(label)} notes`;
        case "previous_visit":
            return `${label} recorded`;
        case "photo":
            return "Patient photo shows";
        default:
            return label;
    }
}

function attributionNoun(type: ContextSourceType, label: string): string {
    switch (type) {
        case "questionnaire":
            return `${label}'s report`;
        case "photo":
            return "the patient's photo";
        default:
            return label;
    }
}

export function formatShortDate(iso: string | null | undefined): string | null {
    if (!iso) {
        return null;
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return null;
    }
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "America/New_York"
    }).format(d);
}

function hintLine(c: ContextCheck): string {
    const clause = c.clause.trim().replace(/\.$/, "");
    const sep = /[?:]/.test(clause) && !/^[a-z]/.test(clause) ? ": " : " ";
    return `${attributionPrefix(c.sourceType, c.sourceLabel)}${sep}${clause}`;
}

function dated(c: ContextCheck): string {
    if (c.sourceType === "previous_visit") {
        return "";
    }
    const d = formatShortDate(c.observedAt);
    return d ? ` (${d})` : "";
}

/**
 * Mirror of the server's `disagreementText`: "Differs from family member's
 * report: 2 falls (Sep 10)", or "Changed since …" for a previous visit. The
 * server writes a model-judged contradiction's text with the same function,
 * from the bare clause the model returns.
 */
function disagreementText(c: ContextCheck, what: string): string {
    const change = c.sourceType === "previous_visit";
    return `${change ? "Changed since" : "Differs from"} ${attributionNoun(c.sourceType, c.sourceLabel)}: ${what}${dated(c)}`;
}

function isAnswered(v: string | undefined | null): boolean {
    if (v == null) {
        return false;
    }
    const s = String(v).trim();
    return s !== "" && s !== "[]";
}

export function evaluateContextChecks(
    checks: ContextCheck[],
    answers: Record<string, string | undefined>,
    questions?: QuestionOptionsLookup | null
): ContextCheck[] {
    const out: ContextCheck[] = [];
    for (const raw of checks) {
        if (raw.questionId == null) {
            continue;
        }
        const question = questions?.get(raw.questionId) ?? null;
        // Values, not labels; legacy `agreeValues` read as affirmed options.
        const check: ContextCheck = { ...raw, expects: expectsOf(raw, question) };
        delete check.agreeValues;
        const answer = answers[String(check.questionId)];
        if (!isAnswered(answer)) {
            out.push({
                ...check,
                kind: "omission",
                text: `Not yet answered — ${lowerFirst(hintLine(check))}`
            });
            continue;
        }
        if (check.expects.length > 0) {
            const failed = failedExpectations(answer as string, check.expects, question);
            if (failed.length === 0) {
                continue;
            }
            out.push({
                ...check,
                kind: check.sourceType === "previous_visit" ? "change" : "contradiction",
                text: disagreementText(check, failed.map((f) => f.clause).join("; "))
            });
            continue;
        }
        if (check.origin === "model") {
            out.push({ ...check, kind: check.kind === "omission" ? "contradiction" : check.kind });
        }
    }
    return out;
}
